import styled from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export const HeroWrapper = styled.section`
  position: relative;
  height: 100vh;
  min-height: 600px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const VideoBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Placeholder kad nema videa */
  /* &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.navy} 0%,
      ${({ theme }) => theme.colors.navyLight} 40%,
      ${({ theme }) => theme.colors.primaryDark} 100%
    );
    z-index: 0;
  } */
`;

export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  /* Radial scrim: darkens center where text sits, keeps edges of video visible */
  background:
    radial-gradient(
      ellipse 70% 60% at 50% 50%,
      rgba(8, 14, 26, 0.55) 0%,
      rgba(8, 14, 26, 0.25) 60%,
      transparent 100%
    ),
    linear-gradient(
      to bottom,
      rgba(26, 35, 50, 0.55) 0%,
      rgba(26, 35, 50, 0.55) 50%,
      rgba(26, 35, 50, 0.85) 100%
    );
`;

export const Content = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 0 ${({ theme }) => theme.spacing.xl};
  max-width: 900px;
`;

export const Eyebrow = styled(motion.p)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primaryLight};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);

  &::before,
  &::after {
    content: "";
    display: inline-block;
    width: 28px;
    height: 1px;
    background: rgba(163, 185, 205, 0.55);
  }
`;

export const Heading = styled(motion.h1)`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(3rem, 9vw, 6.5rem);
  font-weight: ${({ theme }) => theme.fontWeights.light};
  color: ${({ theme }) => theme.colors.white};
  line-height: 1;
  letter-spacing: ${({ theme }) => theme.letterSpacings.tight};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  text-wrap: balance;
  text-shadow:
    0 2px 4px rgba(0, 0, 0, 0.4),
    0 8px 32px rgba(0, 0, 0, 0.45);

  em {
    font-style: italic;
    font-weight: ${({ theme }) => theme.fontWeights.regular};
    color: ${({ theme }) => theme.colors.primaryLight};
    /* luminous inner glow on the brand word */
    text-shadow:
      0 0 32px rgba(163, 185, 205, 0.55),
      0 0 64px rgba(163, 185, 205, 0.25),
      0 2px 4px rgba(0, 0, 0, 0.4);
  }
`;

export const Subheading = styled(motion.p)`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: rgba(255, 255, 255, 0.85);
  line-height: ${({ theme }) => theme.lineHeights.relaxed};
  max-width: 580px;
  margin: 0 auto ${({ theme }) => theme.spacing["2xl"]};
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.45);
`;

export const Actions = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const PrimaryBtn = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  padding: 16px 40px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(112, 141, 171, 0.35);
  }
  @media (max-width: 768px) {
    width: 200px;
    padding-left: 0;
    padding-right: 0;
  }
`;

export const SecondaryBtn = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: ${({ theme }) => theme.letterSpacings.wider};
  text-transform: uppercase;
  padding: 16px 40px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};

  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    border-color: rgba(255, 255, 255, 0.8);
    background: rgba(255, 255, 255, 0.08);
  }
  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
    width: 200px;
  }
`;

export const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing["2xl"]};
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;

  @media (max-height: 750px) {
    bottom: ${({ theme }) => theme.spacing["md"]};
  }
`;

export const ScrollLine = styled(motion.div)`
  width: 1px;
  height: 48px;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.6),
    rgba(255, 255, 255, 0)
  );

  @media (max-height: 750px) {
    height: 40px;
  }
`;

export const ScrollLabel = styled.span`
  font-family: ${({ theme }) => theme.fonts.caps};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  letter-spacing: ${({ theme }) => theme.letterSpacings.widest};
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  @media (max-height: 750px) {
    font-size: 10px;
  }
`;
